import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { transformPropertiesToLocal } from "utils/propertyTransforms";
import { separateAndFormatTenants } from "utils/tenantUtils";
import { toast } from "utils/toast";

import { useCreateLeaseContract } from "hooks/useCreateLeaseContract";
import { useGetProperties } from "hooks/useGetProperties";
import { useGetTenants } from "hooks/useGetTenants";

import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { StepIndicator } from "js/components/ui/step-indicator";

import {
  PropertyRegistration,
  PropertyRegistrationRef,
} from "../Tenants/components/PropertyRegistration";
import { Step1PropertySelection } from "../Tenants/components/Step1PropertySelection";
import { Step2TenantSelection } from "../Tenants/components/Step2TenantSelection";
import { TenantRegistration } from "../Tenants/components/TenantRegistration";
import { STEPS } from "../Tenants/constants";
import { useModalViewTransition } from "../Tenants/hooks/useModalViewTransition";
import { useRentalApplicationForm } from "../Tenants/hooks/useRentalApplicationForm";
import { useStepNavigation } from "../Tenants/hooks/useStepNavigation";
import { Step3GuarantorConfigurationContract } from "./components/Step3GuarantorConfigurationContract";
import { Step4ReviewContract } from "./components/Step4ReviewContract";
import {
  isGuarantorConfigurationValid,
  isPropertySelectionValid,
  isTenantSelectionValid,
} from "../Tenants/validation";

interface CreateLeaseContractModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

// Use only 4 steps: Property, Tenant, Guarantor, Review
const CONTRACT_STEPS = [
  STEPS[0], // Elige Inmueble
  STEPS[1], // Elige Inquilino
  STEPS[2], // ¿Fiador Requerido?
  STEPS[4], // Resumen (Review)
];

const CreateLeaseContractModal = ({
  isOpen,
  onOpenChange,
  onSuccess,
}: CreateLeaseContractModalProps) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    formData,
    updateFormData,
    resetForm,
    properties,
    setProperties,
    selectedProperty,
    newProperty,
    setNewProperty,
    newTenant,
    setNewTenant,
    tenants,
    resetNewTenant,
  } = useRentalApplicationForm();

  const { mutateAsync: createContract, isPending: isCreating } =
    useCreateLeaseContract();

  const { data: fetchedProperties } = useGetProperties();
  const {
    data: fetchedTenants,
    isLoading: tenantsLoading,
    error: tenantsError,
  } = useGetTenants();

  // Process tenants data using utility function
  const { naturalPersons, legalPersons } = fetchedTenants
    ? separateAndFormatTenants(fetchedTenants)
    : { naturalPersons: [], legalPersons: [] };

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const {
    currentStep,
    isAnimating,
    handleNext: originalHandleNext,
    handleBack: originalHandleBack,
    handleStepClick: originalHandleStepClick,
    getStepProgress,
    resetStep,
  } = useStepNavigation(CONTRACT_STEPS.length);

  // Wrap navigation functions to include scroll to top
  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return isPropertySelectionValid(formData);
      case 2:
        return isTenantSelectionValid(formData);
      case 3:
        return isGuarantorConfigurationValid(formData);
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!isStepValid(currentStep)) {
      return;
    }
    originalHandleNext();
    setTimeout(scrollToTop, 50);
  };

  const handleBack = () => {
    originalHandleBack();
    setTimeout(scrollToTop, 50);
  };

  const handleStepClick = (stepNumber: number) => {
    originalHandleStepClick(stepNumber);
    setTimeout(scrollToTop, 50);
  };

  const {
    modalView,
    viewTransition,
    previousView,
    propertySheetStep,
    setPropertySheetStep,
    tenantSheetStep,
    setTenantSheetStep,
    transitionToPropertyRegistration,
    transitionToTenantRegistration,
    transitionBackToRentalApplication,
    resetView,
  } = useModalViewTransition();

  // Ref to access PropertyRegistration methods
  const propertyRegistrationRef = useRef<PropertyRegistrationRef>(null);

  // Check if there's content to scroll and update indicator visibility
  const checkScrollable = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const hasScrollableContent = scrollHeight > clientHeight;
      const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 5;
      setShowScrollIndicator(hasScrollableContent && isNotAtBottom);
    }
  };

  // Scroll to bottom when indicator is clicked
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Set up scroll listener and MutationObserver
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const handleScroll = () => {
        checkScrollable();
      };

      scrollContainer.addEventListener("scroll", handleScroll);

      const observer = new MutationObserver(() => {
        checkScrollable();
      });

      observer.observe(scrollContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });

      setTimeout(checkScrollable, 100);

      const handleResize = () => checkScrollable();
      window.addEventListener("resize", handleResize);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
        observer.disconnect();
      };
    }
  }, [modalView, currentStep, propertySheetStep, tenantSheetStep]);

  // Re-check scrollable content when step changes or modal opens
  useEffect(() => {
    setTimeout(checkScrollable, 100);
    setTimeout(checkScrollable, 500);
  }, [currentStep, propertySheetStep, tenantSheetStep, modalView, isOpen]);

  // Check when form data changes
  useEffect(() => {
    checkScrollable();
    setTimeout(checkScrollable, 100);
  }, [formData.property, formData.tenant, formData.requiresGuarantor]);

  // Update properties when fetched data is available
  useEffect(() => {
    if (fetchedProperties) {
      const transformedProperties =
        transformPropertiesToLocal(fetchedProperties);
      setProperties(transformedProperties);
    }
  }, [fetchedProperties, setProperties]);

  // Set default alternativeJustice to true for contracts
  useEffect(() => {
    if (isOpen && formData.alternativeJustice === null) {
      updateFormData("alternativeJustice", true);
    }
  }, [isOpen]);

  const handleClose = () => {
    resetForm();
    resetStep();
    resetView();
    onOpenChange(false);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (
        modalView === "property-registration" ||
        modalView === "tenant-registration"
      ) {
        transitionBackToRentalApplication();
        return;
      }
      handleClose();
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.property || !formData.tenant) {
        toast.warning("Selecciona un inmueble e inquilino antes de continuar.");
        return;
      }
      setIsSubmitting(true);

      // Determine tenant legal type
      const tenantLegalType =
        formData.tenantType === "moral" ? "legal" : "natural";

      // Build templates array based on selections
      const templates: string[] = [];

      // Tenant form template (REQUIRED)
      if (tenantLegalType === "legal") {
        templates.push("tenant-pm-cja");
      } else {
        templates.push("tenant-pf-cja");
      }

      // Lease contract template (will be determined by backend or default to PF)
      templates.push("lease-contract-pf");

      // Guarantor templates (if selected)
      if (formData.requiresGuarantor) {
        const guarantorLegalType =
          formData.guarantorType === "moral" ? "legal" : "natural";
        if (guarantorLegalType === "legal") {
          templates.push("guarantor-pm-cja");
        } else {
          templates.push("guarantor-pf-cja");
        }

        // Add collateral template if property as guarantee is selected
        if (formData.propertyAsGuarantee) {
          templates.push("collateral");
        }
      }

      // Simplified payload using references
      const contractPayload = {
        tenant_person_id: formData.tenant, // UUID of selected tenant
        property_id: formData.property, // UUID of selected property
        templates: templates,
      };

      const response = await createContract(contractPayload);

      toast.success("Contrato creado exitosamente");

      if (onSuccess) {
        onSuccess();
      }

      // Navigate to contract details page
      if (response?.id) {
        navigate(`/contracts/${response.id}/details`);
      }

      handleClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Error al crear el contrato. Por favor intenta de nuevo.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    if (modalView === "property-registration") {
      return (
        <PropertyRegistration
          ref={propertyRegistrationRef}
          propertySheetStep={propertySheetStep}
          setPropertySheetStep={setPropertySheetStep}
          onCancelClick={transitionBackToRentalApplication}
          onPropertyCreated={(propertyData) => {
            const newPropertyLocal = {
              id: propertyData.id,
              label: propertyData.alias || propertyData.address.street,
              alias: propertyData.alias || "",
              direccion: propertyData.address.street,
              colonia: propertyData.address.neighborhood,
              uso: propertyData.usage || "",
              rental_price: propertyData.rental_price?.toString() || "",
              maintenance_price:
                propertyData.maintenance_price?.toString() || "",
              street: propertyData.address.street,
              exterior_number: propertyData.address.exterior_number,
              interior_number: propertyData.address.interior_number,
              zip_code: propertyData.address.zip_code,
              city: propertyData.address.city,
              state: propertyData.address.state,
            };
            setProperties((prev) => [...prev, newPropertyLocal]);
            updateFormData("property", propertyData.id);
            transitionBackToRentalApplication();
          }}
        />
      );
    }

    if (modalView === "tenant-registration") {
      return (
        <TenantRegistration
          tenantSheetStep={tenantSheetStep}
          newTenant={newTenant}
          setNewTenant={setNewTenant}
          tenantType={formData.tenantType}
          onTenantCreated={(tenantData) => {
            updateFormData("tenant", tenantData.id);
            updateFormData(
              "tenantName",
              tenantData.name || tenantData.legal_entity_name,
            );
            updateFormData(
              "tenantPhone",
              `${tenantData.dial_code} ${tenantData.phone_number}`,
            );
            updateFormData("tenantEmail", tenantData.email);
            resetNewTenant();
            transitionBackToRentalApplication();
          }}
          onError={(error) => {
            toast.error(error);
          }}
          onCancel={() => {
            resetNewTenant();
            transitionBackToRentalApplication();
          }}
        />
      );
    }

    // Main contract flow
    switch (currentStep) {
      case 1:
        return (
          <Step1PropertySelection
            formData={formData}
            updateFormData={updateFormData}
            isAnimating={isAnimating}
            properties={properties}
            selectedProperty={selectedProperty}
            transitionToPropertyRegistration={transitionToPropertyRegistration}
          />
        );
      case 2:
        return (
          <Step2TenantSelection
            formData={formData}
            updateFormData={updateFormData}
            isAnimating={isAnimating}
            tenantsDirectory={tenants}
            naturalPersons={naturalPersons}
            legalPersons={legalPersons}
            tenantsLoading={tenantsLoading}
            tenantsError={tenantsError}
            transitionToTenantRegistration={transitionToTenantRegistration}
          />
        );
      case 3:
        return (
          <Step3GuarantorConfigurationContract
            formData={formData}
            updateFormData={updateFormData}
            isAnimating={isAnimating}
          />
        );
      case 4:
        return (
          <Step4ReviewContract
            formData={formData}
            updateFormData={updateFormData}
            isAnimating={isAnimating}
            properties={properties}
            onNavigateToStep={handleStepClick}
          />
        );
      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    if (modalView === "property-registration") {
      return "Registrar Nuevo Inmueble";
    }
    if (modalView === "tenant-registration") {
      return "Registrar Nuevo Inquilino";
    }
    return "Crear Contrato de Arrendamiento";
  };

  const renderFooter = () => {
    if (modalView !== "rental-application") {
      return null; // Property and Tenant registration have their own footers
    }

    return (
      <DialogFooter className="flex-row items-center justify-between pt-4 border-t border-gray-200 shrink-0 gap-2 sm:justify-between">
        {currentStep === 1 ? (
          <Button
            variant="ghost"
            onClick={handleClose}
            className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </Button>
        ) : (
          <Button
            variant="ghost"
            onClick={handleBack}
            className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
            Regresar
          </Button>
        )}
        <div className="flex items-center gap-2 sm:gap-4">
          <Badge
            variant="secondary"
            className="px-2 py-1 bg-gray-100 text-gray-700 text-[11px] sm:px-2.5 sm:text-xs"
          >
            <span className="sm:hidden">
              {currentStep}/{CONTRACT_STEPS.length}
            </span>
            <span className="hidden sm:inline">
              Paso {currentStep} de {CONTRACT_STEPS.length}
            </span>
          </Badge>
          {currentStep < CONTRACT_STEPS.length ? (
            <Button
              onClick={handleNext}
              disabled={
                isSubmitting ||
                isCreating ||
                isAnimating ||
                !isStepValid(currentStep)
              }
              className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
              <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || isCreating}
              className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || isCreating ? "Creando..." : "Crear Contrato"}
            </Button>
          )}
        </div>
      </DialogFooter>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className="w-full max-w-[calc(100vw-2rem)] sm:max-w-4xl sm:min-w-[56rem] max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-none sm:rounded-lg"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="pb-4 shrink-0">
            <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900">
              {getDialogTitle()}
            </DialogTitle>
            {modalView === "rental-application" && (
              <StepIndicator
                steps={CONTRACT_STEPS}
                currentStep={currentStep}
                onStepClick={handleStepClick}
                getStepProgress={getStepProgress}
              />
            )}
          </DialogHeader>

          <div
            ref={scrollContainerRef}
            className="overflow-y-auto overflow-x-hidden relative flex-1 min-h-0"
          >
            {showScrollIndicator && modalView === "rental-application" && (
              <div className="fixed bottom-24 right-4 z-50">
                <button
                  onClick={scrollToBottom}
                  className="bg-gray-500/80 backdrop-blur text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-1 hover:bg-gray-600/80 transition-colors"
                >
                  <span className="text-xs font-medium">Ver más</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="min-h-[400px]">{renderCurrentStep()}</div>
          </div>

          {renderFooter()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateLeaseContractModal;
